/* eslint-disable max-len */
import { ExclamationCircleTwoTone } from '@ant-design/icons';
import { ConfirmModal } from '@evidia/components';
import type { ExamType } from '@evidia/shared';
import {
	OrderByTrpc,
	STUDENT_GROUP_TEXT,
	StudentState as StudentStateType,
	TrainingType,
	toCzDateString,
} from '@evidia/shared';
import { Alert, Button, Col, DatePicker, Row, Spin, Typography, notification } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { TRAINING_EXTENDED_TYPES_TEXT, TRAINING_TYPES_TEXT } from '../../const/const';
import { devices } from '../../const/devices';
import { DATE_PICKER_FORMAT } from '../../const/forms';
import { ROUTES } from '../../const/routes';
import { useActiveSchoolId } from '../../hooks/useActiveSchoolId';
import { useAppConfig } from '../../hooks/useAppConfig';
import { useIsTablet } from '../../hooks/useBreakpoint';
import { useSystemStore } from '../../stores/SystemStore';
import { isSameOrAfterDay, isSameOrBeforeDay } from '../../utils/date';
import { trpc } from '../../utils/trcp';
import Center from '../Center';
import ExampCheckBox from '../ExamCheckBox';
import ExamResultType from '../ExamResultType';
import ExampType from '../ExampType';
import MainTitle from '../MainTitle';
import SimpleTable from '../SimpleTable/SimpleTable';
import StudentGroup from '../StudentGroup';
import HoverableEditItem from './HoverableEditItem';

const StyledTable = styled.table<{ $border?: boolean }>`
	border: 0 !important;
	box-shadow: none !important;
	border-radius: unset !important;
	padding: 0 !important;
	border-left: ${({ $border }) => ($border ? '1px solid #f0f0f0' : 'none')} !important;
	height: 100% !important;

	tr {
		height: 4em !important;
		text-align: left !important;

		@media ${devices.mobileL} {
			height: 100% !important;

			td {
				margin-top: 0.5em;
				margin-bottom: 0.5em;
			}
		}
	}

	tr:nth-child(2) {
		background-color: #fafafa;
	}

	td:first-child {
		border-right: 1px solid #f0f0f0;
		width: 2em;
	}
`;

const FooterDiv = styled.div`
	display: flex;
	justify-content: space-between;
	padding-top: 1em;
	float: right;
`;

const ResultWrapper = styled.div`
	margin-right: 1em;
	/* span {
		margin-right: 1em;
	} */

	@media ${devices.mobileL} {
		/* height: 10em !important; */
	}
`;

const StyledRow = styled(Row)`
	.item-title {
		font-family: 'Inter';
		font-style: normal;
		font-weight: 600;
		font-size: 11px;
		line-height: 22px;
		/* identical to box height, or 200% */
		letter-spacing: 0.05em;
		text-transform: uppercase;

		/* Neutral/7 */
		color: #8c8c8c;
	}
`;

const SectionTitle = styled(MainTitle)`
	padding-top: 1rem;
`;

const TableWrapper = styled.div`
	td {
		padding: 0 16px !important;
	}
`;
interface Props {
	id: string;
	showLink?: boolean;
}

const StudentCard: React.FC<Props> = ({ id }) => {
	const config = useAppConfig();
	const isTablet = useIsTablet();
	const [todayDate] = useState(dayjs().subtract(config.dayPeerExamInactive, 'day').toDate());
	const removeStudent = trpc.student.removeStudent.useMutation();
	const { activeSchoolId } = useActiveSchoolId();
	const getInterpreters = trpc.interpreter.getInterpreters.useQuery({ schoolId: activeSchoolId });

	const navigate = useNavigate();
	const [date, setDate] = useState<Dayjs | null>(null);

	const [saving, setSaving] = useState(false);
	const setStudentStateM = trpc.student.setStudentState.useMutation();
	const setDateOfInProgress = trpc.student.setDateOfInProgress.useMutation();
	const setDateOfFinished = trpc.student.setDateOfFinished.useMutation();

	const setUserCardId = useSystemStore(state => state.setUserCardId);

	const examsData = trpc.exam.exams.useQuery({
		search: {
			schoolId: activeSchoolId,
			dateGte: todayDate,
		},
		orderBy: OrderByTrpc.ASC,
	});

	const hideStudentCard = (): void => {
		setUserCardId(undefined);
	};

	const data = trpc.student.student.useQuery({
		id,
		schoolId: activeSchoolId,
	});

	const student = data.data;
	const setStudentState = async (state: StudentStateType): Promise<void> => {
		setSaving(true);

		let r;
		if (state === StudentStateType.IN_PROGRESS) {
			r = await setDateOfInProgress.mutateAsync({
				studentsIds: [id],
				schoolId: activeSchoolId,
				date: date?.toDate() ?? null,
			});
		} else if (state === StudentStateType.FINISHED) {
			await setDateOfFinished.mutateAsync({
				studentsIds: [id],
				schoolId: activeSchoolId,
				date: date?.toDate() ?? null,
			});
		} else {
			r = await setStudentStateM.mutateAsync({
				state,
				studentsIds: [id],
				schoolId: activeSchoolId,
				date: date?.toDate() ?? null,
			});
		}
		if (state === StudentStateType.IN_PROGRESS && r?.reportId) {
			navigate(`${ROUTES.APP.REPORTING_DETAIL}/${r.reportId}`);
			setUserCardId(undefined);
			setSaving(false);
			return;
		}
		await data.refetch();
	};

	if (data.isLoading || examsData.isLoading) {
		return (
			<Center hasPadding>
				<Spin />
			</Center>
		);
	}

	if (!student) {
		return <Alert message="Student nebyl nalezen" type="error" showIcon />;
	}

	const age = dayjs().diff(dayjs(student.studentInfo!.birthDate), 'year');
	const { birthDate, phone, email } = student.studentInfo!; // dayjs(student.studentInfo!.birthDate).format('DD.MM.YYYY');

	const studentOnExams = student.studentOnExam ?? [];
	const exams = (examsData.data ?? []).filter(e => {
		const studentOnExam = studentOnExams.filter(soe => soe.examId === e.id);

		if (studentOnExam.length === 0 && dayjs().startOf('day').isAfter(e.date)) {
			return false;
		}
		return true;
	});

	return (
		<div>
			<StyledRow gutter={16}>
				<Col className="gutter-row" span={isTablet ? 24 : 13}>
					<HoverableEditItem>
						<MainTitle level={4}>Informace o žákovi</MainTitle>
					</HoverableEditItem>
					<HoverableEditItem
						form={{
							property: 'birthDate',
							value: dayjs(birthDate),
							type: 'studentInfo',
							studentId: id,
							schoolId: activeSchoolId,
							onDone: data.refetch,
						}}
					>
						<h1 className="item-title">datum narození</h1>
						<p>{`${toCzDateString(birthDate)} (${age} let)`}</p>
					</HoverableEditItem>
					<HoverableEditItem
						form={{
							property: 'birthPlace',
							value: student.studentInfo!.birthPlace,
							type: 'studentInfo',
							studentId: id,
							schoolId: activeSchoolId,
							onDone: data.refetch,
						}}
					>
						<h1 className="item-title">Místo narození</h1>
						<p>{student.studentInfo!.birthPlace}</p>
					</HoverableEditItem>
					<HoverableEditItem
						form={{
							property: 'citizenship',
							value: student.studentInfo!.citizenship,
							type: 'studentInfo',
							studentId: id,
							schoolId: activeSchoolId,
							onDone: data.refetch,
						}}
					>
						<h1 className="item-title">Státní občanství</h1>
						<p>{student.studentInfo!.citizenship}</p>
					</HoverableEditItem>
					{student.studentInfo!.citizenship !== 'Česká republika' && (
						<HoverableEditItem
							form={{
								property: 'interpreter',
								value: student.studentInterpreter?.interpreterId,
								type: 'studentInterpreter',
								studentId: id,
								schoolId: activeSchoolId,
								onDone: data.refetch,
								data: {
									getInterpreters: {
										data: getInterpreters.data as any,
										isLoading: getInterpreters.isLoading,
									},
								},
							}}
						>
							<h1 className="item-title">Tlumočník</h1>
							<p>
								{student.studentInterpreter
									? `${student.studentInterpreter?.interpreter?.interpreterInfo?.firstName} ${student.studentInterpreter?.interpreter?.interpreterInfo?.lastName}`
									: '-'}
							</p>
						</HoverableEditItem>
					)}
					<HoverableEditItem
						form={{
							property: 'docId',
							value: student.studentInfo!.docId,
							type: 'studentInfo',
							studentId: id,
							schoolId: activeSchoolId,
							onDone: data.refetch,
						}}
					>
						<h1 className="item-title">Doklad totožnosti</h1>
						<p>{student.studentInfo!.docId}</p>
					</HoverableEditItem>
					<HoverableEditItem
						form={{
							property: 'carIdNumber',
							value: student.studentStudyInfo!.carIdNumber ?? '',
							type: 'studentStudyInfo',
							studentId: id,
							schoolId: activeSchoolId,
							onDone: data.refetch,
						}}
					>
						<h1 className="item-title">Číslo řidičského průkazu</h1>
						<p>{student.studentStudyInfo!.carIdNumber ?? '-'}</p>
					</HoverableEditItem>
					<HoverableEditItem
						form={{
							property: ['street', 'streetCode', 'postCode', 'city'],
							value: {
								street: student.studentAddress!.street,
								streetCode: student.studentAddress!.streetCode,
								postCode: student.studentAddress!.postCode,
								city: student.studentAddress!.city,
							},
							type: 'studentAddress',
							studentId: id,
							schoolId: activeSchoolId,
							onDone: data.refetch,
						}}
					>
						<h1 className="item-title">Adresa</h1>
						<p>
							{`${student.studentAddress!.street} ${student.studentAddress!.streetCode}, ${student.studentAddress!.postCode} ${student.studentAddress!.city}`}
						</p>
					</HoverableEditItem>

					<HoverableEditItem
						form={{
							property: 'phone',
							value: phone,
							type: 'studentInfo',
							studentId: id,
							schoolId: activeSchoolId,
							onDone: data.refetch,
						}}
					>
						<h1 className="item-title">Telefon</h1>
						<p>{phone}</p>
					</HoverableEditItem>

					<HoverableEditItem
						form={{
							property: 'email',
							value: email,
							type: 'studentInfo',
							studentId: id,
							schoolId: activeSchoolId,
							onDone: data.refetch,
						}}
					>
						<h1 className="item-title">E-mail</h1>
						<p>{email}</p>
					</HoverableEditItem>
				</Col>
				<Col className="gutter-row" span={isTablet ? 0 : 3} />
				<Col className="gutter-row" span={isTablet ? 24 : 8}>
					<HoverableEditItem>
						<MainTitle level={4}>Údaje o výcviku</MainTitle>
					</HoverableEditItem>
					<HoverableEditItem
						form={{
							property: 'registrationNumber',
							value: student.registrationNumber,
							type: 'student',
							studentId: id,
							schoolId: activeSchoolId,
							onDone: data.refetch,
						}}
					>
						<h1 className="item-title">Evidenční číslo</h1>
						<p>{student.registrationNumber}</p>
					</HoverableEditItem>
					{student.studentStudyInfo!.trainingType === TrainingType.BASIC ? (
						<HoverableEditItem>
							<h1 className="item-title">Skupina</h1>
							<p>
								{student.studentStudyInfo!.group.map((g, i) => (
									<React.Fragment key={g}>
										<StudentGroup type={g} />
										{i !== student.studentStudyInfo!.group.length - 1 && ', '}
									</React.Fragment>
								))}
							</p>
						</HoverableEditItem>
					) : (
						<>
							<HoverableEditItem>
								<h1 className="item-title">Skupina stávající</h1>
								<p>
									{student.studentStudyInfo!.currentGroups.map((g, i) => (
										<React.Fragment key={g}>
											<StudentGroup type={g} />
											{i !== student.studentStudyInfo!.currentGroups.length - 1 && ', '}
										</React.Fragment>
									))}
								</p>
							</HoverableEditItem>
							<HoverableEditItem>
								<h1 className="item-title">Skupina žádaná</h1>
								<p>
									{student.studentStudyInfo!.group.map((g, i) => (
										<React.Fragment key={g as any}>
											<StudentGroup type={g} />
											{i !== student.studentStudyInfo!.group.length - 1 && ', '}
										</React.Fragment>
									))}
								</p>
							</HoverableEditItem>
						</>
					)}
					{student.state !== StudentStateType.NOT_STARTED &&
						(() => (
							<HoverableEditItem
							// form={{
							// 	property: 'date',
							// 	value: log?.date ? czDateStringToDate(log.date) : 'error',
							// 	type: 'log',
							// 	studentId: id,
							// 	schoolId: activeSchoolId,
							// 	meta: { id: log?.id },
							// 	onDone: data.refetch,
							// }}
							>
								<h1 className="item-title">Datum zahájení</h1>
								<p>
									{student.dateOfInProgress ? toCzDateString(student.dateOfInProgress) : 'Nezahájen'}
								</p>
							</HoverableEditItem>
						))()}
					{[
						StudentStateType.FINISHED,
						StudentStateType.DONE,
						StudentStateType.TO_EXAM,
						StudentStateType.EXAM_CORRECTION,
					].includes(student.state as StudentStateType) &&
						(() => {
							if (!student.dateOfFinished) {
								return (
									<>
										<h1 className="item-title">Datum ukončení</h1>
										<DatePicker
											onChange={v => setDate(v as any)}
											format={DATE_PICKER_FORMAT}
											placeholder="Vyberte datum"

											// size="large"
										/>
										<Button
											size="large"
											type="primary"
											// disabled={footerButtonDisabled}
											onClick={async () => {
												await setStudentState(StudentStateType.FINISHED);
												await data.refetch();
											}}
											// loading={loaderType === StudentState.Finished}
										>
											Ukončit výcvik
										</Button>
									</>
								);
							}
							return (
								<HoverableEditItem
									form={{
										property: 'dateOfFinished',
										value: student.dateOfFinished ? dayjs(student.dateOfFinished) : dayjs(),
										type: 'student',
										studentId: id,
										schoolId: activeSchoolId,
										onDone: data.refetch,
									}}
								>
									<h1 className="item-title">Datum ukončení</h1>
									<p>
										{student.dateOfFinished ? toCzDateString(student.dateOfFinished) : 'Neukončen'}
									</p>
								</HoverableEditItem>
							);
						})()}
					<HoverableEditItem>
						<h1 className="item-title">Druh výcviku</h1>
						<p>{TRAINING_TYPES_TEXT[student.studentStudyInfo!.trainingType]}</p>
					</HoverableEditItem>
					<HoverableEditItem>
						<h1 className="item-title">Typ výcviku</h1>
						<p>{TRAINING_EXTENDED_TYPES_TEXT[student.studentStudyInfo!.trainingTypeExtended]}</p>
						{student.state === StudentStateType.NOT_STARTED && (
							<div>
								<Button
									type="primary"
									onClick={() => setStudentState(StudentStateType.IN_PROGRESS)}
									size="large"
									disabled={saving}
									loading={saving}
									data-testid="studentSetInProgress"
								>
									Zahájit výcvik
								</Button>
								<DatePicker
									size="large"
									onChange={v => setDate(v as any)}
									format={DATE_PICKER_FORMAT}
									placeholder="Vyberte datum"
								/>
							</div>
						)}
					</HoverableEditItem>
					<HoverableEditItem noBorder>
						<SectionTitle level={4}>Poznámka</SectionTitle>
					</HoverableEditItem>
					<HoverableEditItem
						form={{
							property: 'note',
							value: data.data?.studentStudyInfo?.note,
							type: 'studentStudyInfo',
							studentId: id,
							schoolId: activeSchoolId,
							onDone: data.refetch,
						}}
					>
						<h1 className="item-title">Interní poznámka</h1>
						<p>{data.data?.studentStudyInfo?.note ?? '-'}</p>
					</HoverableEditItem>
				</Col>
			</StyledRow>
			<br />
			{student.state !== StudentStateType.NOT_STARTED && (
				<TableWrapper>
					<Typography.Title level={4}>Zkoušky</Typography.Title>
					<SimpleTable
						showHeader={false}
						border
						count={{ data: exams.length, loading: false }}
						data={{ data: exams, loading: false }}
						columns={[
							{
								title: 'Datum',
								key: 'date',
								render: (exam: any) => {
									if (isSameOrAfterDay(exam.date)) {
										return (
											<Link
												to={`${ROUTES.APP.EXAMS_RESULTS}/${exam.id}`}
												onClick={hideStudentCard}
											>
												<Button type="link">{toCzDateString(exam.date)}</Button>
											</Link>
										);
									}

									if (isSameOrBeforeDay(exam.date)) {
										return (
											<Link
												to={`${ROUTES.APP.EXAMS_DETAIL}/${exam.id}`}
												onClick={hideStudentCard}
											>
												<Button type="link">{toCzDateString(exam.date)}</Button>
											</Link>
										);
									}

									return exam.date;
								},
							},
							{
								title: 'Místo',
								key: 'place',
								render: (exam: any) => exam.place,
							},
							{
								title: 'Actions',
								key: 'actions',
								render: (exam: any) => {
									const { type } = exam;

									return (
										<StyledTable $border>
											{student.studentStudyInfo!.group.map(g => (
												<tr key={g}>
													<td>{STUDENT_GROUP_TEXT[g]}</td>
													{type.map((t: ExamType) =>
														(() => {
															// console.log({ g, t });
															const getScore = (): number => {
																let score = 1;

																for (let i = 0; i < studentOnExams.length; i++) {
																	const soe = studentOnExams[i];

																	if (soe.examId === exam.id) {
																		break;
																	}
																	if (soe.group === g && soe.type === t) {
																		// console.log(soe);
																		score += 1;
																	}
																}

																return score;
															};
															const score = getScore();
															// console.log(studentOnExams);
															const studentOnExamsByType = studentOnExams.filter(
																soe =>
																	soe.type === t &&
																	soe.group === g &&
																	soe.examId === exam.id,
															)?.[0];
															const { result } = studentOnExamsByType ?? {};
															// const result = targetExam?.results[t] as
															// 	| ExamResult
															// 	| undefined;

															// const score = prevExams.reduce((acc, curr) => {
															// 	const r = curr?.results[t] as ExamResult | undefined;
															// 	if (r && r !== ExamResult.DidNotTakePlace) {
															// 		return acc + 1;
															// 	}

															// 	return acc;
															// }, 1);

															if (result) {
																return (
																	<td>
																		<ResultWrapper>
																			<ExamResultType type={result} short />

																			<b>
																				<ExampType
																					tag={false}
																					type={t as unknown as ExamType}
																				/>
																				{` ${score}/${config.maximumNumberOfAttemptsPerExam}`}
																			</b>
																		</ResultWrapper>
																	</td>
																);
															}

															return (
																// eslint-disable-next-line jsx-a11y/control-has-associated-label
																<td>
																	<ResultWrapper>
																		<ExampCheckBox
																			forceDisable={
																				![
																					StudentStateType.TO_EXAM,
																					StudentStateType.EXAM_CORRECTION,
																					StudentStateType.FINISHED,
																				].includes(
																					student.state as any,
																					// eslint-disable-next-line react/jsx-indent
																				)
																			}
																			studentId={student.id}
																			examId={exam.id}
																			type={t as any}
																			group={g as any}
																			studentOnExam={studentOnExams as any}
																			logs={student.logs as any}
																			exams={exams as any}
																			refetch={async () => {
																				await data.refetch();
																			}}
																		/>
																	</ResultWrapper>
																</td>
															);
														})(),
													)}
												</tr>
											))}
										</StyledTable>
									);
								},
							},
						]}
						footer={
							<>
								{student.state === StudentStateType.IN_PROGRESS ? (
									<>
										<DatePicker
											onChange={v => setDate(v as any)}
											format={DATE_PICKER_FORMAT}
											placeholder="Vyberte datum"

											// size="large"
										/>
										<Button
											size="large"
											type="primary"
											// disabled={footerButtonDisabled}
											onClick={() => setStudentState(StudentStateType.FINISHED)}
											// loading={loaderType === StudentState.Finished}
										>
											Ukončit výcvik
										</Button>
									</>
								) : undefined}
							</>
						}
					/>
				</TableWrapper>
			)}
			<FooterDiv>
				<ConfirmModal
					title={
						<>
							<ExclamationCircleTwoTone twoToneColor="#ff0000" />
							{' Odstranit žáka'}
						</>
					}
					description="Opravdu chcete odstranit žáka a odstranit tím i všechna jeho data?"
					onConfirm={async () => {
						await removeStudent.mutateAsync({
							studentId: student.id,
							schoolId: activeSchoolId,
						});
						notification.success({
							message: 'Žák byl úspěšně odstraněn',
							closeIcon: false,
						});
						navigate(ROUTES.APP.ROOT);
					}}
					okText="Ano"
					cancelText="Zrušit"
				>
					<Button type="link" danger>
						Odstranit žáka
					</Button>
				</ConfirmModal>
			</FooterDiv>

			{/* {student.logs.length > 0 && (
				<>
					<br />
					<Collapse
						items={[
							{
								icon: null,
								label: 'Log akcí',
								children: (
									<Timeline
										mode="left"
										items={student.logs.map(log => ({
											label: `Datum události: ${toCzDateString(dayjs(log.createdAt))}`,
											children: <div>{`${log.date} - ${STUDENT_LOG_TYPE_TEXT[log.type]}`}</div>,
										}))}
									/>
								),
							},
						]}
					/>
				</>
			)} */}
		</div>
	);
};

export default StudentCard;
